import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Footer() {
  const { t } = useTranslation();
  const cols = [
    { heading: t("footer.col_network"), links: [
      ["What is Circularity Global?", "/about"],
      [t("nav.network"), "/network"],
      [t("nav.hubs"), "/hubs"],
      [t("nav.marketplace"), "/marketplace"],
      ["Atlas", "/atlas"],
      ["Resource Intelligence", "/intelligence"],
      ["Methodology Center", "/methodology"],
      ["Network Proof", "/network-proof"],
      [t("nav.credits"), "/credits"],
      [t("nav.dpp"), "/dpp"],
    ]},
    { heading: t("footer.col_ai"), links: [
      ["Waste-to-Resource Engine", "/ai-tools/waste-to-resource"],
      ["Hub Builder", "/ai-tools/hub-builder"],
      ["Opportunity Explorer", "/ai-tools/opportunity-explorer"],
      ["Digital Twin", "/ai-tools/digital-twin"],
      ["Calculators", "/ai-tools/calculators"],
    ]},
    { heading: t("footer.col_for"), links: [
      ["Governments", "/for/governments"],
      ["Landowners", "/for/landowners"],
      ["Waste / Feedstock Sources", "/for/waste-sources"],
      ["Recycling Technology", "/for/recycling-tech"],
      ["Software Partners", "/for/software-partners"],
      ["Offtake Buyers", "/for/offtake-buyers"],
      ["Investors", "/for/investors"],
      ["Fintech & Banks", "/for/fintech"],
      ["Corporates", "/for/corporates"],
    ]},
    { heading: t("footer.col_company"), links: [
      [t("nav.foundation"), "/foundation"],
      [t("nav.academy"), "/academy"],
      ["Investor Portal", "/portal"],
      ["Contact / Join", "/contact"],
    ]},
  ];
  return (
    <footer className="mt-32 border-t border-border/60 bg-background" data-testid="site-footer">
      <div className="container-x py-20">
        {/* Canonical "Why two products?" accordion — present on every page */}
        <WhyTwoProducts />

        <div className="grid gap-12 md:grid-cols-[1.4fr_3fr]">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2.5" data-testid="footer-logo">
                <BrandLogo variant="symbol" size={34} testId="footer-logo-symbol" />
                <BrandLogo variant="wordmark" size={32} testId="footer-logo-wordmark" />
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm text-muted-foreground leading-relaxed">{t("footer.tagline")}</p>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-1 text-sm text-signal-lime hover:gap-2 transition-all" data-testid="footer-join-cta">
              {t("footer.join_cta")} <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
            {cols.map((c) => (
              <div key={c.heading}>
                <p className="eyebrow mb-3">{c.heading}</p>
                <ul className="space-y-2">
                  {c.links.map(([label, to]) => (
                    <li key={to}>
                      <Link to={to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-border/60 pt-6">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Circularity Global. {t("footer.rights")}</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground" data-testid="footer-legal">
            <Link to="/legal/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/legal/risk-disclaimer" className="hover:text-foreground transition-colors">Risk</Link>
            <Link to="/legal/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">{t("footer.version")}</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Why two products? · canonical investor framing, every footer ────────
function WhyTwoProducts() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-14 border border-border/60 rounded-2xl bg-card/40" data-testid="footer-why-two-products">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left"
        data-testid="footer-why-toggle"
      >
        <span className="flex items-baseline gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-signal-lime">Investor note</span>
          <span className="text-sm md:text-base text-foreground">Why two products? — Station and Hub explained.</span>
        </span>
        <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? "rotate-180 text-signal-lime" : "text-muted-foreground"}`} />
      </button>
      {open && (
        <div className="px-5 pb-6 md:px-6 md:pb-7 border-t border-border/60" data-testid="footer-why-content">
          <p className="mt-5 max-w-3xl text-sm text-muted-foreground">
            Both products share the same backbone — land, communities, local entrepreneurs, biproduct offtakers, carbon credits, CSR, investors and MACCIA&rsquo;s OVOE alignment. <span className="text-foreground">Granullo</span> handles programmable money movement across both. Only what we call <span className="text-foreground">Circularity</span> — and which tech layer plugs in — differs.
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <article className="border border-border bg-background/40 rounded-xl p-4" data-testid="footer-meaning-hub">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-signal-lime">Circularity · in a Hub</p>
              <p className="mt-2 text-sm text-foreground">Circular economy in its original sense. <span className="font-semibold">Waste → wealth.</span> Every byproduct becomes someone else&rsquo;s input.</p>
              <Link to="/network?view=hub" className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.18em] text-signal-lime hover:gap-2 transition-all" data-testid="footer-hub-cta">
                Open Hub OS <ArrowUpRight size={11}/>
              </Link>
            </article>
            <article className="border border-border bg-background/40 rounded-xl p-4" data-testid="footer-meaning-station">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-signal-lime">Circularity · in a Station</p>
              <p className="mt-2 text-sm text-foreground">A shared-resource capital cycle of the kind the world&rsquo;s largest infrastructure players already operate amongst themselves — land, power, compute and capital pooled into one closed flow of shared ownership and shared revenue. Brought to rural communities.</p>
              <Link to="/network?view=station" className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.18em] text-signal-lime hover:gap-2 transition-all" data-testid="footer-station-cta">
                Open Station OS <ArrowUpRight size={11}/>
              </Link>
            </article>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            See the canonical architecture on <Link to="/network" className="text-signal-lime hover:underline">/network</Link>.
          </p>
        </div>
      )}
    </div>
  );
}
